import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { BsSearch } from 'react-icons/bs/index.js';
import config from '../../config';
import DataTable from '../widgets/DataTable';
import './auditoriaIngresos.css';
import { formatDateTime, getPresetDates, PRESETS, renderOrigen, useAvisoInicioAuditoria } from './auditoriaIngresosComun';
import { toast } from '../widgets/alerts/Toast';

const renderTipoUsuario = (tipo) => {
  const t = String(tipo || '');
  let label = 'Usuario';
  let bg = '#f1f5f9';
  let color = '#475569';
  if (t === '1' || t === '0') {
    label = 'Administrador';
    bg = '#dbeafe';
    color = '#1e40af';
  } else if (t === '2') {
    label = 'Gestor';
    bg = '#fef3c7';
    color = '#92400e';
  } else if (t === '3') {
    label = 'Vendedor';
    bg = '#f3e8ff';
    color = '#6b21a8';
  } else if (t === '4') {
    label = 'Gestor Aprobador';
    bg = '#e0e7ff';
    color = '#3730a3';
  }
  return (
    <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '8px', backgroundColor: bg, color: color }}>
      {label}
    </span>
  );
};

const renderActividad = (row) => {
  const isActivo = Number(row.ACTIVO_EN_INTERVALO) === 1;
  const count = Number(row.TOTAL_INGRESOS_RANGO) || 0;
  if (isActivo) {
    return <span className="ai-badge ai-badge-on">Activo ({count} {count === 1 ? 'ingreso' : 'ingresos'})</span>;
  }
  return <span className="ai-badge ai-badge-mute">Inactivo en periodo</span>;
};

const renderUltimoIngreso = (row) => {
  const enRango = formatDateTime(row.ULTIMO_INGRESO_RANGO);
  if (enRango) {
    return <span style={{ fontWeight: '600', color: '#1e293b' }}>{enRango}</span>;
  }
  const historico = formatDateTime(row.ULTIMO_INGRESO_HISTORICO);
  if (historico) {
    return (
      <div>
        <span style={{ color: '#64748b', fontSize: '12px' }}>{historico}</span>
        <span style={{ display: 'block', fontSize: '10px', color: '#94a3b8' }}>(Fuera del periodo)</span>
      </div>
    );
  }
  return <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>Sin ingresos registrados</span>;
};

const COLUMNS = [
  {
    key: 'USUARIO',
    label: 'Usuario',
    sortable: true,
    render: (val) => <span style={{ fontWeight: '700', color: '#1b2f55' }}>{val || '--'}</span>
  },
  {
    key: 'NOMBRE_COMPLETO',
    label: 'Nombre / Razón Social',
    sortable: true,
    render: (val) => val || <span style={{ color: '#94a3b8' }}>No registrado</span>
  },
  {
    key: 'TIPO_USUARIO',
    label: 'Tipo Usuario',
    sortable: true,
    render: (val) => renderTipoUsuario(val)
  },
  {
    key: 'ULTIMO_INGRESO_RANGO',
    label: 'Último Ingreso',
    sortable: true,
    render: (_, row) => renderUltimoIngreso(row)
  },
  {
    key: 'ACTIVO_EN_INTERVALO',
    label: 'Estado en Periodo',
    sortable: true,
    render: (_, row) => renderActividad(row)
  },
  {
    key: 'ULTIMO_ORIGEN',
    label: 'Último Canal',
    render: (val) => renderOrigen(val)
  },
  {
    key: 'ULTIMO_DISPOSITIVO',
    label: 'Último Dispositivo / Navegador',
    render: (val) => <span style={{ fontSize: '12px', color: '#475569' }}>{val || '--'}</span>
  }
];

const renderPuedeIngresar = (_, row) => {
  const habilitado = Number(row.PUEDE_INGRESAR) === 1;
  return <span className={`ai-badge ${habilitado ? 'ai-badge-on' : 'ai-badge-mute'}`}>{habilitado ? 'Habilitado' : 'Inhabilitado'}</span>;
};

const COLUMNS_CLIENTES = [
  {
    key: 'DOCUMENTO_CLIENTE',
    label: 'NIT / Documento',
    sortable: true,
    render: (val) => <span style={{ fontWeight: '700', color: '#1b2f55' }}>{val || '--'}</span>
  },
  {
    key: 'NOMBRE_CLIENTE',
    label: 'Cliente',
    sortable: true,
    render: (val) => val || <span style={{ color: '#94a3b8' }}>No registrado</span>
  },
  {
    key: 'PUEDE_INGRESAR',
    label: 'Acceso',
    sortable: true,
    render: renderPuedeIngresar
  },
  {
    key: 'ULTIMO_INGRESO_RANGO',
    label: 'Último Ingreso',
    sortable: true,
    render: (_, row) => renderUltimoIngreso(row)
  },
  {
    key: 'INGRESOS_RANGO',
    label: 'Ingresos en Periodo',
    sortable: true,
    render: (val) => Number(val) || 0
  },
  {
    key: 'FALLIDOS_RANGO',
    label: 'Intentos Fallidos',
    sortable: true,
    render: (val) => Number(val) || 0
  },
  {
    key: 'ULTIMO_DISPOSITIVO',
    label: 'Último Dispositivo / Navegador',
    render: (val) => <span style={{ fontSize: '12px', color: '#475569' }}>{val || '--'}</span>
  }
];

const VISTAS = [
  { key: 'usuarios', label: 'Usuarios internos' },
  { key: 'clientes', label: 'Clientes' }
];

function IngresosUsuarios() {
  useAvisoInicioAuditoria();
  const defaultDates = getPresetDates(30);
  const [vista, setVista] = useState('usuarios');
  const [politica, setPolitica] = useState(null);
  const [columnasPendientes, setColumnasPendientes] = useState(false);
  const [filtroPuede, setFiltroPuede] = useState('todos');
  const [mostrar, setMostrar] = useState('actividad');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablePending, setTablePending] = useState(false);
  const [activePreset, setActivePreset] = useState('mes');
  const [fechaInicio, setFechaInicio] = useState(defaultDates.inicio);
  const [fechaFin, setFechaFin] = useState(defaultDates.fin);
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const company = sessionStorage.getItem('company');
  const tipo = sessionStorage.getItem('tipo');

  const lastUrl = useRef('');

  const fetchActividad = useCallback((overrideParams = {}, forzar = false) => {
    const fInicio = overrideParams.fInicio !== undefined ? overrideParams.fInicio : fechaInicio;
    const fFin = overrideParams.fFin !== undefined ? overrideParams.fFin : fechaFin;
    const user = overrideParams.user !== undefined ? overrideParams.user : filtroUsuario;
    const estado = overrideParams.estado !== undefined ? overrideParams.estado : filtroEstado;
    const puede = overrideParams.puede !== undefined ? overrideParams.puede : filtroPuede;
    const mostrarClientes = overrideParams.mostrar !== undefined ? overrideParams.mostrar : mostrar;

    const esClientes = vista === 'clientes';
    const queryParams = new URLSearchParams();
    if (fInicio) queryParams.append('fecha_inicio', fInicio);
    if (fFin) queryParams.append('fecha_fin', fFin);
    if (user) queryParams.append(esClientes ? 'cliente' : 'usuario', user);
    if (!esClientes && estado && estado !== 'todos') queryParams.append('estado_actividad', estado);
    if (esClientes && mostrarClientes === 'todos') queryParams.append('mostrar', 'todos');
    if (esClientes && puede !== 'todos') queryParams.append('acceso', puede);

    const url = esClientes
      ? `${config.servBackEnd}/portalclientes/actividad?${queryParams.toString()}`
      : `${config.servBackEnd}/users/logs/actividad-usuarios?${queryParams.toString()}`;

    // Evita repetir la misma consulta (p. ej. preset + efecto de filtros); "Buscar" fuerza la consulta
    if (!forzar && lastUrl.current === url) return;
    lastUrl.current = url;
    setLoading(true);

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        empresa: company,
        tipo: tipo
      }
    })
      .then((res) => res.json())
      .then((resData) => {
        setColumnasPendientes(Boolean(resData.columnasPendientes));
        setPolitica(esClientes && resData.politica ? resData.politica : null);
        if (resData.tablePending) {
          setTablePending(true);
          setData([]);
        } else if (resData.ok && Array.isArray(resData.data)) {
          setTablePending(false);
          setData(resData.data);
          if (esClientes && resData.truncado) {
            const tope = Number(resData.limite).toLocaleString('es-CO');
            toast({ title: 'Resultado limitado', description: `Mostrando los primeros ${tope} clientes. Use el buscador o el filtro de Acceso para acotar.`, type: 'warning', position: 'bottom-center', duration: 6 });
          }
        } else {
          setData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error al cargar consolidado de actividad:', err);
        setLoading(false);
      });
  }, [company, tipo, vista, fechaInicio, fechaFin, filtroUsuario, filtroEstado, filtroPuede, mostrar]);

  // Espera 400 ms tras el último cambio de filtros (evita una consulta por cada letra); la primera carga es inmediata
  useEffect(() => {
    const t = setTimeout(() => fetchActividad(), lastUrl.current ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchActividad]);

  // Avisos de configuración pendiente (toast global)
  useEffect(() => {
    if (tablePending) {
      toast({ title: 'Configuración pendiente', description: 'La tabla de auditoría LOG_INGRESO_USUARIOS aún no se ha creado en la base de datos.', type: 'neutral-blue', position: 'bottom-center', duration: 6 });
    }
  }, [tablePending]);

  useEffect(() => {
    if (columnasPendientes) {
      toast({ title: 'Configuración pendiente', description: 'El control de ingreso de clientes aún no está configurado en la base de datos.', type: 'neutral-blue', position: 'bottom-center', duration: 6 });
    }
  }, [columnasPendientes]);

  // Manejo de Presets Rápidos
  const handlePresetSelect = (presetKey, days) => {
    setActivePreset(presetKey);
    const { inicio, fin } = getPresetDates(days);
    setFechaInicio(inicio);
    setFechaFin(fin);
    fetchActividad({ fInicio: inicio, fFin: fin });
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    fetchActividad({}, true);
  };

  const handleLimpiarFiltros = () => {
    setActivePreset('');
    setFechaInicio('');
    setFechaFin('');
    setFiltroUsuario('');
    setFiltroEstado('todos');
    setFiltroPuede('todos');
    setMostrar('actividad');
    fetchActividad({ fInicio: '', fFin: '', user: '', estado: 'todos', puede: 'todos', mostrar: 'actividad' });
  };


  return (
    <div className="ai-root" style={{ padding: '0 0 20px', backgroundColor: '#f8fafc', minHeight: 'calc(100vh - 80px)' }}>
      {/* Banner de Título */}
      <div
        style={{
          background: 'var(--ai-grad)',
          color: 'white',
          padding: '1.4rem 2rem',
          borderRadius: '16px',
          boxShadow: '0 4px 14px rgba(27, 47, 85, 0.25)',
          marginBottom: '10px',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px'
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <h2 style={{ color: 'white', margin: 0, fontWeight: '700', fontSize: '1.5rem', letterSpacing: '-0.4px' }}>
            Auditoría de Ingresos de Usuarios
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#94a3b8' }}>
            Consolidado del último ingreso a la plataforma por cada cuenta de usuario y control de actividad en el intervalo seleccionado
          </p>
        </div>
      </div>

      <div className="ai-content">
      {/* Selector de vista: usuarios internos / clientes del portal */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {VISTAS.map((v) => {
            const isActive = vista === v.key;
            return (
              <button
                key={v.key}
                type="button"
                onClick={() => { setLoading(true); setVista(v.key); }}
                style={{
                  fontSize: '0.8rem',
                  fontWeight: isActive ? '700' : '500',
                  padding: '5px 16px',
                  borderRadius: '16px',
                  border: `1px solid ${isActive ? '#1b2f55' : '#cbd5e1'}`,
                  background: isActive ? 'var(--ai-grad)' : '#ffffff',
                  color: isActive ? '#ffffff' : '#334155',
                  cursor: 'pointer'
                }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
        {vista === 'clientes' && politica && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '12px', fontWeight: '600' }}>
            <span className={`ai-badge ${politica.activo ? 'ai-badge-on' : 'ai-badge-off'}`}>
              {politica.activo ? 'Ingreso de clientes activo' : 'Ingreso de clientes desactivado'}
            </span>
            <span style={{ padding: '3px 10px', borderRadius: '12px', backgroundColor: '#dbeafe', color: '#1e40af' }}>
              {politica.modo === 1 ? 'Solo clientes habilitados' : 'Todos los clientes'}
            </span>
            {politica.modo === 1 && (
              <span style={{ padding: '3px 10px', borderRadius: '12px', backgroundColor: '#f1f5f9', color: '#475569' }}>
                {politica.habilitados}{politica.cupo !== null ? ` / ${politica.cupo}` : ''} habilitados
              </span>
            )}
          </div>
        )}
      </div>

      {/* Panel de Filtros Estilo gestionNomina */}
      <div className="dt-wrapper" style={{ overflow: 'visible', padding: '18px 22px', marginBottom: '4px' }}>
        <Form onSubmit={handleSearchSubmit}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f1f5f9'
            }}
          >
            <h6 style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', margin: 0, letterSpacing: '0.5px' }}>
              FILTROS DE BÚSQUEDA TEMPORAL
            </h6>

            {/* Presets Rápidos de Tiempo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginRight: '4px' }}>
                Periodo rápido:
              </span>
              {PRESETS.map((p) => {
                const isActive = activePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => handlePresetSelect(p.key, p.days)}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: isActive ? '700' : '500',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      border: `1px solid ${isActive ? '#1b2f55' : '#cbd5e1'}`,
                      background: isActive ? 'var(--ai-grad)' : '#ffffff',
                      color: isActive ? '#ffffff' : '#334155',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                      boxShadow: isActive ? '0 2px 6px rgba(27, 47, 85, 0.25)' : 'none'
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                  Fecha Inicio
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fechaInicio}
                  onChange={(e) => {
                    setFechaInicio(e.target.value);
                    setActivePreset('custom');
                  }}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                  Fecha Fin
                </Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={fechaFin}
                  onChange={(e) => {
                    setFechaFin(e.target.value);
                    setActivePreset('custom');
                  }}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                  {vista === 'clientes' ? 'Filtrar Cliente' : 'Filtrar Usuario'}
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder={vista === 'clientes' ? 'Buscar por NIT o nombre...' : 'Buscar por usuario o nombre...'}
                  value={filtroUsuario}
                  onChange={(e) => setFiltroUsuario(e.target.value)}
                  style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px' }}
                />
              </Form.Group>
            </Col>

            {vista === 'clientes' && (
              <Col xs={12} sm={6} md>
                <Form.Group className="mb-0">
                  <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                    Mostrar
                  </Form.Label>
                  <Form.Control
                    as="select"
                    size="sm"
                    value={mostrar}
                    onChange={(e) => setMostrar(e.target.value)}
                    style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px', backgroundColor: '#fff' }}
                  >
                    <option value="actividad">Con actividad</option>
                    <option value="todos">Todos los clientes</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            )}

            <Col xs={12} sm={6} md>
              <Form.Group className="mb-0">
                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                  {vista === 'clientes' ? 'Acceso' : 'Estado de Actividad'}
                </Form.Label>
                {vista === 'clientes' ? (
                  <Form.Control
                    as="select"
                    size="sm"
                    value={filtroPuede}
                    onChange={(e) => setFiltroPuede(e.target.value)}
                    style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px', backgroundColor: '#fff' }}
                  >
                    <option value="todos">Todos los clientes</option>
                    <option value="si">Solo habilitados</option>
                    <option value="no">Solo inhabilitados</option>
                  </Form.Control>
                ) : (
                  <Form.Control
                    as="select"
                    size="sm"
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    style={{ borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.82rem', height: '34px', backgroundColor: '#fff' }}
                  >
                    <option value="todos">Todos los usuarios</option>
                    <option value="activos">Solo activos en periodo</option>
                    <option value="inactivos">Solo inactivos en periodo</option>
                  </Form.Control>
                )}
              </Form.Group>
            </Col>

            <Col xs={12} sm={6} md="auto" className="d-flex">
              <button
                type="submit"
                className="dt-btn-create"
                style={{ width: '132px', justifyContent: 'center', height: '34px', padding: '0 8px', fontSize: '0.8rem' }}
              >
                <BsSearch size={12} /> Buscar
              </button>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
            <button
              type="button"
              onClick={handleLimpiarFiltros}
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#64748b',
                background: 'none',
                border: 'none',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: '2px 6px'
              }}
            >
              Limpiar Filtros
            </button>
          </div>
        </Form>
      </div>

      {/* Tabla Consolidada de Último Ingreso por Usuario */}
      <div style={{ marginTop: '-16px' }}>
        {vista === 'clientes' ? (
          <DataTable
            data={data}
            columns={COLUMNS_CLIENTES}
            loading={loading}
            searchKeys={['DOCUMENTO_CLIENTE', 'NOMBRE_CLIENTE', 'ULTIMO_DISPOSITIVO']}
            searchPlaceholder="Búsqueda rápida de clientes..."
            emptyText="No se encontraron clientes con los filtros aplicados"
            defaultItemsPerPage={15}
          />
        ) : (
          <DataTable
            data={data}
            columns={COLUMNS}
            loading={loading}
            searchKeys={['USUARIO', 'NOMBRE_COMPLETO', 'ULTIMO_DISPOSITIVO', 'ULTIMO_ORIGEN']}
            searchPlaceholder="Búsqueda rápida de usuarios..."
            emptyText="No se encontraron usuarios con los filtros aplicados"
            defaultItemsPerPage={15}
          />
        )}
      </div>
      </div>
    </div>
  );
}

export default IngresosUsuarios;



// WEBPACK FOOTER //
// ./src/components/configuracion/ingresosUsuarios.jsx